import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, PackagePlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STORAGE = ["Room Temp", "2-8C", "Frozen"];
const IVD_RUO = ["IVD", "RUO"];
// Item Master is for batch/lot-tracked consumables only (Reagent, Consumable, Control,
// Calibrator, Cleaning & Disinfection, ...). Fixed assets (Equipment, Computer, Printer,
// Mobile, ...) live in the separate Asset Register — they don't have batches/expiry/reorder
// points, they're tracked one-by-one by serial number & location instead.
const fieldGroupOf = (categories, name) => categories.find((c) => c.name === name)?.field_group || "generic";

export default function ItemMaster() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["items", "consumable"], queryFn: () => api.get("/items?kind=consumable").then((r) => r.data) });
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => api.get("/locations").then((r) => r.data) });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const consumableCategories = categories.filter((c) => !["equipment_calibrated", "equipment_asset"].includes(c.field_group));

  const [itemOpen, setItemOpen] = useState(false);
  const [grnOpen, setGrnOpen] = useState(false);
  const blankItem = {
    name: "", category: "", uom: "", storage_condition: "2-8C", hsn_code: "",
    reorder_point: 0, reorder_qty: 0, pack_size: "",
    manufacturer: "", kit_size: "", ivd_ruo: "",
  };
  const [form, setForm] = useState(blankItem);
  const isReagentLike = fieldGroupOf(categories, form.category) === "reagent";
  const blankGrn = { item_id: "", location_id: "", lot_number: "", mfg_date: "", expiry_date: "", vendor_name: "", qty: 0, grn_ref: "" };
  const [grn, setGrn] = useState(blankGrn);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(blankItem);
  const editIsReagentLike = fieldGroupOf(categories, editForm.category) === "reagent";
  const [deleteTarget, setDeleteTarget] = useState(null);

  const createItem = async () => {
    if (!form.name.trim() || !form.category) { toast.error("Name and category are required"); return; }
    try {
      await api.post("/items", { ...form, reorder_point: Number(form.reorder_point), reorder_qty: Number(form.reorder_qty) });
      toast.success("Item added to master");
      setItemOpen(false); setForm(blankItem);
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEditItem = (i) => {
    setEditTarget(i);
    setEditForm({ ...blankItem, ...i });
  };

  const saveEditItem = async () => {
    if (!editTarget) return;
    if (!editForm.name.trim() || !editForm.category) { toast.error("Name and category are required"); return; }
    try {
      await api.put(`/items/${editTarget._id}`, {
        ...editForm, reorder_point: Number(editForm.reorder_point), reorder_qty: Number(editForm.reorder_qty),
      });
      toast.success("Item updated");
      setEditTarget(null);
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/items/${deleteTarget._id}`);
      toast.success("Item permanently deleted");
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDeleteTarget(null);
  };

  const receiveStock = async () => {
    try {
      await api.post("/stock/in", { ...grn, qty: Number(grn.qty) });
      toast.success("Stock received (GRN) with batch/lot & expiry");
      setGrnOpen(false); setGrn(blankGrn);
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="items-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-navy">Item Master</h2>
          <p className="text-slate-500 text-sm mt-1">Catalog of reagents, consumables, controls &amp; calibrators. Looking for lab equipment, computers, or printers? See Asset Register.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={grnOpen} onOpenChange={setGrnOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="open-grn-button" className="border-[#0a8f8f] text-[#0a8f8f] hover:bg-[#0a8f8f] hover:text-white transition-colors duration-200">
                <PackagePlus size={16} className="mr-2" /> Receive Stock (GRN)
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl">
              <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Goods Received Note — Stock In</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Item</Label>
                  <Select value={grn.item_id} onValueChange={(v) => setGrn({ ...grn, item_id: v })}>
                    <SelectTrigger data-testid="grn-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {items.map((i) => <SelectItem key={i._id} value={i._id}>{i.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Select value={grn.location_id} onValueChange={(v) => setGrn({ ...grn, location_id: v })}>
                    <SelectTrigger data-testid="grn-location-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {locations.map((l) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Lot / Batch No.</Label><Input data-testid="grn-lot" value={grn.lot_number} onChange={(e) => setGrn({ ...grn, lot_number: e.target.value })} /></div>
                <div><Label>Mfg Date</Label><Input type="date" value={grn.mfg_date} onChange={(e) => setGrn({ ...grn, mfg_date: e.target.value })} /></div>
                <div><Label>Expiry Date</Label><Input type="date" data-testid="grn-expiry" value={grn.expiry_date} onChange={(e) => setGrn({ ...grn, expiry_date: e.target.value })} /></div>
                <div><Label>Vendor</Label><Input value={grn.vendor_name} onChange={(e) => setGrn({ ...grn, vendor_name: e.target.value })} /></div>
                <div><Label>Quantity</Label><Input type="number" data-testid="grn-qty" value={grn.qty} onChange={(e) => setGrn({ ...grn, qty: e.target.value })} /></div>
                <div><Label>GRN Reference</Label><Input value={grn.grn_ref} onChange={(e) => setGrn({ ...grn, grn_ref: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={receiveStock} data-testid="grn-submit" style={{ background: "#042C53" }} className="text-white">Receive &amp; Stock In</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={itemOpen} onOpenChange={setItemOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-add-item-button" style={{ background: "#042C53" }} className="text-white">
                <Plus size={16} className="mr-2" /> Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif text-navy text-xl">New Item</DialogTitle></DialogHeader>

              <div className="pb-4 mb-1 border-b border-slate-200">
                <Label className="text-navy font-semibold">1. Category</Label>
                <p className="text-xs text-slate-500 mb-2">Pick this first — the fields below adjust to match its specification.</p>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="item-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {consumableCategories.filter((c) => c.active).map((c) => <SelectItem key={c._id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Name</Label><Input data-testid="item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div>
                  <Label>Storage</Label>
                  <Select value={form.storage_condition} onValueChange={(v) => setForm({ ...form, storage_condition: v })}>
                    <SelectTrigger data-testid="item-storage"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{STORAGE.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Unit of Measure</Label><Input data-testid="item-uom" value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="kit / vial / box" /></div>
                <div><Label>Pack Size</Label><Input value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} /></div>
                <div><Label>HSN Code</Label><Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} /></div>
                <div><Label>Reorder Point</Label><Input type="number" data-testid="item-reorder-point" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} /></div>
                <div><Label>Reorder Qty</Label><Input type="number" value={form.reorder_qty} onChange={(e) => setForm({ ...form, reorder_qty: e.target.value })} /></div>
              </div>

              {isReagentLike && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <Label className="text-navy font-semibold">Reagent / Control / Calibrator details</Label>
                  <p className="text-xs text-slate-500 mb-2">Lot number &amp; expiry are captured per-batch at Receive Stock (GRN) — these are the item-level specs.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Manufacturer</Label><Input data-testid="item-manufacturer" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
                    <div><Label>Kit Size</Label><Input value={form.kit_size} onChange={(e) => setForm({ ...form, kit_size: e.target.value })} placeholder="e.g. 100 tests/kit" /></div>
                    <div>
                      <Label>IVD / RUO</Label>
                      <Select value={form.ivd_ruo} onValueChange={(v) => setForm({ ...form, ivd_ruo: v })}>
                        <SelectTrigger data-testid="item-ivd-ruo"><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent className="bg-white">{IVD_RUO.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button onClick={createItem} data-testid="item-submit" style={{ background: "#042C53" }} className="text-white">Save Item</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">UOM</th>
                <th className="px-4 py-3 font-semibold">Storage</th>
                <th className="px-4 py-3 font-semibold">Pack</th>
                <th className="px-4 py-3 font-semibold">HSN</th>
                <th className="px-4 py-3 font-semibold text-right">Reorder Pt</th>
                <th className="px-4 py-3 font-semibold text-right">Edit</th>
                <th className="px-4 py-3 font-semibold text-right">Delete</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i, idx) => (
                <tr key={i._id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-200" data-testid={`item-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-navy">
                    {i.name}
                    {fieldGroupOf(categories, i.category) === "reagent" && (i.manufacturer || i.ivd_ruo) && (
                      <div className="text-xs font-normal text-slate-400">{[i.manufacturer, i.ivd_ruo].filter(Boolean).join(" · ")}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{i.category}</td>
                  <td className="px-4 py-3 text-slate-600">{i.uom}</td>
                  <td className="px-4 py-3 text-slate-600">{i.storage_condition}</td>
                  <td className="px-4 py-3 text-slate-600">{i.pack_size}</td>
                  <td className="px-4 py-3 text-slate-600">{i.hsn_code}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{i.reorder_point}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => openEditItem(i)} data-testid={`item-edit-${idx}`}
                      className="text-slate-400 hover:text-teal hover:bg-slate-100"
                    >
                      <Pencil size={16} />
                    </Button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => setDeleteTarget(i)} data-testid={`item-delete-${idx}`}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No items yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Edit {editTarget?.name}</DialogTitle></DialogHeader>

          <div className="pb-4 mb-1 border-b border-slate-200">
            <Label className="text-navy font-semibold">Category</Label>
            <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
              <SelectTrigger data-testid="edit-item-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="bg-white">
                {consumableCategories.filter((c) => c.active || c.name === editForm.category).map((c) => <SelectItem key={c._id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name</Label><Input data-testid="edit-item-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div>
              <Label>Storage</Label>
              <Select value={editForm.storage_condition} onValueChange={(v) => setEditForm({ ...editForm, storage_condition: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STORAGE.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Unit of Measure</Label><Input value={editForm.uom} onChange={(e) => setEditForm({ ...editForm, uom: e.target.value })} /></div>
            <div><Label>Pack Size</Label><Input value={editForm.pack_size} onChange={(e) => setEditForm({ ...editForm, pack_size: e.target.value })} /></div>
            <div><Label>HSN Code</Label><Input value={editForm.hsn_code} onChange={(e) => setEditForm({ ...editForm, hsn_code: e.target.value })} /></div>
            <div><Label>Reorder Point</Label><Input type="number" value={editForm.reorder_point} onChange={(e) => setEditForm({ ...editForm, reorder_point: e.target.value })} /></div>
            <div><Label>Reorder Qty</Label><Input type="number" value={editForm.reorder_qty} onChange={(e) => setEditForm({ ...editForm, reorder_qty: e.target.value })} /></div>
          </div>

          {editIsReagentLike && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <Label className="text-navy font-semibold">Reagent / Control / Calibrator details</Label>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Manufacturer</Label><Input value={editForm.manufacturer} onChange={(e) => setEditForm({ ...editForm, manufacturer: e.target.value })} /></div>
                <div><Label>Kit Size</Label><Input value={editForm.kit_size} onChange={(e) => setEditForm({ ...editForm, kit_size: e.target.value })} /></div>
                <div>
                  <Label>IVD / RUO</Label>
                  <Select value={editForm.ivd_ruo} onValueChange={(v) => setEditForm({ ...editForm, ivd_ruo: v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent className="bg-white">{IVD_RUO.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={saveEditItem} data-testid="edit-item-submit" style={{ background: "#042C53" }} className="text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the item from the Item Master entirely — it cannot be undone.
              Only allowed when it has no stock batches, transactions, or requisitions
              linked to it. If it's been used, delete will be blocked — edit it instead
              if the details are just wrong.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="item-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete} data-testid="item-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
